import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { reportsApi } from '../services/reportsApi';
import { ExpensesByPropertyReport, ReportCatalogItem, ReportPropertyOption, ReportType } from '../types';

export const useReports = () => {
  const [reportTypes, setReportTypes] = useState<Array<{ id: ReportType; label: string }>>([]);
  const [reports, setReports] = useState<ReportCatalogItem[]>([]);
  const [selectedType, setSelectedType] = useState<ReportType>('expense');
  const [selectedReportId, setSelectedReportId] = useState<'expense-by-property'>('expense-by-property');
  const [properties, setProperties] = useState<ReportPropertyOption[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [reportResult, setReportResult] = useState<ExpensesByPropertyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);

  const loadCatalog = useCallback(async () => {
    startLoading('reports-catalog', 'Loading reports...');
    try {
      const response = await reportsApi.getCatalog();
      setReportTypes(response.reportTypes);
      setReports(response.reports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report catalog');
    } finally {
      stopLoading('reports-catalog');
    }
  }, [startLoading, stopLoading]);

  const loadProperties = useCallback(async () => {
    startLoading('reports-properties', 'Loading report properties...');
    try {
      const response = await reportsApi.getExpenseProperties();
      setProperties(response.properties);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report properties');
    } finally {
      stopLoading('reports-properties');
    }
  }, [startLoading, stopLoading]);

  useEffect(() => {
    loadCatalog();
    loadProperties();
  }, [loadCatalog, loadProperties]);

  const loadYearsForProperty = useCallback(
    async (propertyId: number) => {
      setYears([]);
      setSelectedYear(null);
      setReportResult(null);
      startLoading('reports-years', 'Loading years...');
      try {
        const response = await reportsApi.getExpenseYears(propertyId);
        setYears(response.years);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load available years');
      } finally {
        stopLoading('reports-years');
      }
    },
    [startLoading, stopLoading]
  );

  const runExpensesByPropertyReport = useCallback(
    async (propertyId: number, year: number) => {
      startLoading('reports-run', 'Generating report...');
      setError(null);
      try {
        const report = await reportsApi.getExpensesByProperty(propertyId, year);
        setReportResult(report);
      } catch (err) {
        setReportResult(null);
        setError(err instanceof Error ? err.message : 'Failed to generate report');
      } finally {
        stopLoading('reports-run');
      }
    },
    [startLoading, stopLoading]
  );

  const clearReportResult = useCallback(() => {
    setReportResult(null);
  }, []);

  const downloadHtml = useCallback(
    async (propertyId: number, year: number) => {
      startLoading('reports-download', 'Downloading HTML report...');
      try {
        const blob = await reportsApi.downloadExpensesByPropertyHtml(propertyId, year);
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `expense-by-property-${propertyId}-${year}.html`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download HTML report');
      } finally {
        stopLoading('reports-download');
      }
    },
    [startLoading, stopLoading]
  );

  return {
    reportTypes,
    reports,
    selectedType,
    setSelectedType,
    selectedReportId,
    setSelectedReportId,
    properties,
    years,
    selectedPropertyId,
    setSelectedPropertyId,
    selectedYear,
    setSelectedYear,
    reportResult,
    error,
    globalLoading,
    loadingMessage,
    loadYearsForProperty,
    runExpensesByPropertyReport,
    downloadHtml,
    clearReportResult
  };
};
